if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[data-country-search-results], #sr-country-search-results'),
          countryList: this.querySelector('[data-deferred-country-list]'),
          countryData: this.querySelector('script[data-country-options]'),
          checkIconTemplate: this.querySelector('template[data-localization-check-icon]'),
        };

        // Add event listeners only after DOM is ready
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(() => this.init());
        } else {
          setTimeout(() => this.init(), 0);
        }
      }

      init() {
        // Add event listeners with null checks
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.addEventListener('click', this.onContainerClick.bind(this));
        
        if (this.elements.button) {
          this.elements.button.addEventListener('click', this.openSelector.bind(this));
        }

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

      }

      escapeHtml(value) {
        return String(value === null || value === undefined ? '' : value).replace(/[&<>"']/g, (character) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[character]));
      }

      buildCountryItem(country, checkIcon, showCurrencies, rootUrl) {
        const isCurrent = Boolean(country.current);
        const currentAttribute = isCurrent ? ' aria-current="true"' : '';
        const checkClass = isCurrent ? '' : ' class="visibility-hidden"';
        const currencyClass = `localization-form__currency motion-reduce${showCurrencies ? '' : ' hidden'}`;

        return `
          <li class="disclosure__item" tabindex="-1">
            <a
              class="link link--text disclosure__link caption-large focus-inset"
              href="${this.escapeHtml(rootUrl)}"
              role="option"
              data-value="${this.escapeHtml(country.isoCode)}"
              id="${this.escapeHtml(country.name)}"
              ${currentAttribute}
            >
              <span${checkClass}>${checkIcon}</span>
              <span class="country">${this.escapeHtml(country.name)}</span>
              <span class="${currencyClass}">
                ${this.escapeHtml(country.currencyIsoCode)}
                ${this.escapeHtml(country.currencySymbol)}
              </span>
            </a>
          </li>
        `;
      }

      buildCountryListMarkup(countries, className, ariaLabel, checkIcon, showCurrencies, rootUrl) {
        if (!countries || countries.length === 0) return '';

        const labelAttribute = ariaLabel ? ` aria-label="${this.escapeHtml(ariaLabel)}"` : '';
        return `
          <ul role="list" class="list-unstyled ${className}"${labelAttribute}>
            ${countries.map((country) => this.buildCountryItem(country, checkIcon, showCurrencies, rootUrl)).join('')}
          </ul>
        `;
      }

      hydrateDeferredCountries() {
        const countryList = this.elements.countryList;
        if (!countryList || countryList.dataset.hydrated === 'true') return;

        let data;
        try {
          data = JSON.parse(this.elements.countryData?.textContent || '{}');
        } catch (error) {
          countryList.dataset.hydrated = 'error';
          return;
        }

        const checkIcon = this.elements.checkIconTemplate?.innerHTML?.trim() || '';
        const showCurrencies = countryList.dataset.showCurrencies === 'true';
        const rootUrl = data.rootUrl || '/';
        const popularLabel = countryList.dataset.popularLabel || '';
        let markup = '';

        if (data.showPopularCountries) {
          markup += this.buildCountryListMarkup(
            data.popularCountries || [],
            'popular-countries',
            popularLabel,
            checkIcon,
            showCurrencies,
            rootUrl
          );
        }

        markup += this.buildCountryListMarkup(
          data.countries || [],
          'countries',
          '',
          checkIcon,
          showCurrencies,
          rootUrl
        );

        countryList.innerHTML = markup;
        countryList.dataset.hydrated = 'true';
      }

      onContainerClick(event) {
        const selectedItem = event.target.closest('a[data-value]');
        if (!selectedItem || !this.contains(selectedItem)) return;

        this.onItemClick(event, selectedItem);
      }

      hidePanel() {
        if (this.elements.button) {
          this.elements.button.setAttribute('aria-expanded', 'false');
        }
        if (this.elements.panel) {
          this.elements.panel.setAttribute('hidden', true);
        }
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        
        document.body.classList.remove('overflow-hidden-mobile');
        
        const menuDrawer = document.querySelector('.menu-drawer');
        if (menuDrawer) {
          menuDrawer.classList.remove('country-selector-open');
        }
        
        if (this.header) {
          this.header.preventHide = false;
        }
      }

      onContainerKeyDown(event) {
        this.hydrateDeferredCountries();
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        
        if (focusableItems.length === 0) return;
        
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            if (itemToFocus) itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            if (itemToFocus) itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (!this.elements.button || this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            if (this.elements.button) this.elements.button.focus();
            break;
          case 'SPACE':
            if (!this.elements.button || this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event, selectedItem = event.currentTarget) {
        event.preventDefault();
        const form = this.querySelector('form');
        if (this.elements.input) {
          this.elements.input.value = selectedItem.dataset.value;
        }
        if (form) form.submit();
      }

      openSelector() {
        this.hydrateDeferredCountries();
        if (this.elements.button) {
          this.elements.button.focus();
        }
        if (this.elements.panel) {
          this.elements.panel.toggleAttribute('hidden');
        }
        if (this.elements.button) {
          this.elements.button.setAttribute(
            'aria-expanded',
            (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
          );
        }
        
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        
        const menuDrawer = document.querySelector('.menu-drawer');
        if (menuDrawer) {
          menuDrawer.classList.add('country-selector-open');
        }
      }

      closeSelector(event) {
        if (
          (event.target && event.target.classList.contains('country-selector__overlay')) ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = 0;

        if (this.elements.resetButton) {
          this.elements.resetButton.classList.toggle('hidden', !searchValue);
        }

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryElement = item.querySelector('.country');
          if (countryElement) {
            const countryName = this.normalizeString(countryElement.textContent);
            if (countryName.indexOf(searchValue) > -1) {
              item.parentElement.classList.remove('hidden');
              visibleCountries++;
            } else {
              item.parentElement.classList.add('hidden');
            }
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings?.countrySelectorSearchCount?.replace(
            '[count]',
            visibleCountries
          ) || `${visibleCountries} results`;
        }

        const countrySelector = this.querySelector('.country-selector');
        if (countrySelector) countrySelector.scrollTop = 0;
        
        const countryList = this.querySelector('.country-selector__list');
        if (countryList) countryList.scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.focus();
        }
      }

      onSearchFocus() {
        if (this.elements.searchIcon) {
          this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
        }
      }

      onSearchBlur() {
        if (this.elements.search && !this.elements.search.value && this.elements.searchIcon) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
